<template>
  <div class="view" v-if="canView || canEdit || canDelete" v-cloak>
    <GridPanelHeader
      :title="$t('common.warehouses')"
      :grid-ref="$refs.smWarehousesGrid"
      :show-add="canEdit"
      :show-filter="false"
      @add="newObject"
      :show-refresh="true"
    />

    <GridFilter v-if="false" :grid-ref="$refs.smWarehousesGrid" :visible="filterPanelVisible" :title="$t('common.filter')"> </GridFilter>

    <DxDataGrid
      ref="smWarehousesGrid"
      :data-source="smWarehousesDataSource"
      :column-min-width="50"
      :show-column-lines="true"
      :show-row-lines="false"
      :show-borders="true"
      :column-auto-width="true"
      :allow-column-reordering="true"
      :allow-column-resizing="true"
      :row-alternation-enabled="true"
      :remote-operations="gridRemoteOperations"
      :element-attr="gridAttributes"
      :load-panel="{ enabled: true }"
      :no-data-text="$t('common.no_data_text')"
      @editing-start="onEditingStart"
      @exporting="onExporting"
      @option-changed="onOptionChanged"
    >
      <DxColumn
        data-field="name"
        :caption="$t('common.name')"
        data-type="string"
        alignment="left"
        :allow-sorting="true"
        :filter-operations="['contains']"
        :width="240"
      />
      <DxColumn
        data-field="transactions_count"
        :caption="$t('stock_management.warehouses.transactions_count')"
        data-type="number"
        alignment="left"
        :allow-sorting="true"
        :allow-filtering="false"
        :min-width="160"
      />
      <DxColumn
        data-field="department.name"
        :caption="$t('unit_name')"
        data-type="string"
        alignment="left"
        :allow-sorting="true"
        :filter-operations="['contains']"
        :min-width="180"
      />
      <DxColumn
        data-field="is_active"
        :caption="$t('common.active')"
        data-type="number"
        :allow-sorting="true"
        :allow-filtering="true"
        :filter-operations="['=']"
        :min-width="140"
        :calculateCellValue="(row) => getActiveStatusName(row.is_active)"
        cell-template="isActiveViewTemplate"
      >
        <DxLookup :data-source="active_list" value-expr="id" display-expr="label" />
      </DxColumn>
      <template #isActiveViewTemplate="{ data }">
        <span>{{ getActiveStatusName(data.data.is_active) }}</span>
      </template>
      <DxColumn
        data-field="created_at"
        :caption="$t('common.created_at')"
        data-type="date"
        alignment="left"
        :allow-sorting="true"
        :format="getDxGridDefaultDateFormat"
        :min-width="140"
      />
      <DxColumn
        data-field="updated_at"
        :caption="$t('common.last_update')"
        data-type="date"
        alignment="left"
        :allow-sorting="true"
        :format="updatedAtDateTimeFormat"
        :min-width="160"
      />
      <DxColumn
        :width="70"
        alignment="center"
        :fixed="true"
        fixed-position="right"
        :allow-sorting="false"
        :allow-filtering="false"
        :show-in-column-chooser="false"
        cell-template="actionsCell"
      />
      <template #actionsCell="{ data }">
        <DxActionMenu
          :grid-ref="$refs.smWarehousesGrid"
          row-key="uuid"
          :row="data.data"
          :can-edit="canEdit"
          :can-delete="canDelete"
          :allow-deleting="allowDeleting"
          :edit-action="onEdit"
        />
      </template>

      <DxEditing mode="row" :allow-updating="false" :allow-adding="false" :allow-deleting="false" :confirm-delete="true" :use-icons="true">
        <DxTexts
          :confirm-delete-message="
            $t('common.confirm_delete', {
              title: $t('common.warehouses'),
            })
          "
          :confirm-delete-title="$t('common.confirm_delete_title')"
          :deleteRow="$t('common.delete')"
        />
      </DxEditing>
      <DxPaging :page-size="15" />
      <DxPager
        :visible="true"
        :allowed-page-sizes="pageSizes"
        display-mode="compact"
        :info-text="$t('common.page_info')"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="true"
      />
      <DxFilterRow :visible="true" apply-filter="auto" />
      <DxGroupPanel :visible="false" :empty-panel-text="$t('common.group_panel_placeholder')" />
      <DxColumnChooser :enabled="false" mode="select" />
      <DxColumnFixing :enabled="true" />
      <DxExport :enabled="false" :allow-export-selected-data="false" />
    </DxDataGrid>
  </div>
  <div v-else>
    <forbidden></forbidden>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import CustomStore from "devextreme/data/custom_store";
import {
  DxColumn,
  DxDataGrid,
  DxFilterRow,
  DxPager,
  DxPaging,
  DxEditing,
  DxGroupPanel,
  DxColumnChooser,
  DxColumnFixing,
  DxExport,
  DxTexts,
  DxLookup,
} from "devextreme-vue/data-grid";
import { canDelete, canEdit, canView } from "@/helpers/Permissions";
import Forbidden from "~/pages/errors/Forbidden.vue";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";
import GridFilter from "@/components/DxGrid/GridFilter.vue";
import DxActionMenu from "@/components/DxGrid/DxActionMenu.vue";
import { exportDataGrid } from "devextreme/excel_exporter";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver-es";
import { format } from "date-fns";
import { handleError } from "@/helpers/generalActions";

export default {
  name: "SMWarehouseList",
  middleware: ["auth", "check-company-admin"],
  data() {
    return {
      filterPanelVisible: false,
      active_list: [
        { id: 1, label: this.$t("common.active") },
        { id: 0, label: this.$t("common.inactive") },
      ],
      smWarehousesDataSource: new CustomStore({
        key: "uuid",
        load: async (loadOptions) => {
          const dataSource = {
            data: [],
            totalCount: 0,
          };

          try {
            const pageLimit = loadOptions.isLoadingAll ? 99999 : loadOptions.take;
            const pageNumber = (loadOptions.skip + loadOptions.take) / pageLimit;

            const payload = {
              page: pageNumber,
              limit: pageLimit,
              filter: loadOptions.filter,
              sort: loadOptions.sort,
            };

            const response = await this.getSmWarehouses(payload);
            dataSource.data = response?.data ?? [];
            dataSource.totalCount = response?.pagination?.total ?? 0;
            return dataSource;
          } catch (error) {
            console.error("Error fetching warehouses:", error);
            throw error;
          }
        },
        remove: this.onDelete,
      }),
      gridRemoteOperations: {
        paging: true,
        filtering: true,
        sorting: true,
      },
      pageSizes: [15, 50, 100],
    };
  },
  components: {
    Forbidden,
    GridPanelHeader,
    GridFilter,
    DxDataGrid,
    DxColumn,
    DxPaging,
    DxPager,
    DxFilterRow,
    DxEditing,
    DxGroupPanel,
    DxColumnChooser,
    DxColumnFixing,
    DxExport,
    DxActionMenu,
    DxTexts,
    DxLookup,
  },
  computed: {
    ...mapGetters({
      departments: "company/departments",
      getDxGridDefaultDateFormat: "settings/getDxGridDefaultDateFormat",
    }),
    gridAttributes() {
      return {
        class: "smagria-grid sm-warehouses-grid",
      };
    },
    updatedAtDateTimeFormat() {
      return this.getDxGridDefaultDateFormat ? `${this.getDxGridDefaultDateFormat} HH:mm` : "yyyy-MM-dd HH:mm";
    },
    canView() {
      return canView(this);
    },
    canEdit() {
      return canEdit(this);
    },
    canDelete() {
      return canDelete(this);
    },
  },
  methods: {
    ...mapActions({
      getSmWarehouses: "smWarehouses/getSmWarehouses",
      deleteSmWarehouse: "smWarehouses/deleteSmWarehouse",
      getDepartments: "company/getDepartments",
    }),
    newObject() {
      this.$router.push({
        path: "/stock-management/sm-warehouses/add",
      });
    },
    allowDeleting(row) {
      return this.canDelete && row && !(row.transactions_count);
    },
    getActiveStatusName(value) {
      return this.active_list.find((item) => item.id == value)?.label ?? "";
    },
    onEditingStart(e) {
      e.cancel = true;
      if (e && e.data) {
        this.onEdit(e.data);
      }
    },
    onEdit(row) {
      if (!row || !row.uuid) return;
      this.$router.push({ path: `/stock-management/sm-warehouses/${row.uuid}` });
    },
    async onDelete(rowUUid) {
      if (!rowUUid) return;
      try {
        await this.deleteSmWarehouse({ uuid: rowUUid });
        this.reloadGrid();
      } catch (e) {
        handleError(this, e);
      }
    },
    reloadGrid() {
      const instance = this.$refs?.smWarehousesGrid?.instance;
      if (instance) {
        instance.getDataSource()?.reload?.();
      }
    },
    onExporting(e) {
      e.fileName = "SMWarehouses_" + format(new Date(), "yyyy_MM_dd_HH_mm_ss");
      e.cancel = true;

      if (e.format === "xlsx") {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("SMWarehouses");

        exportDataGrid({
          component: e.component,
          worksheet,
          autoFilterEnabled: true,
          customizeCell: ({ gridCell, excelCell }) => {
            if (!gridCell?.data) return;
            if (gridCell?.column?.dataField === "is_active") {
              excelCell.value = this.getActiveStatusName(gridCell?.data?.is_active);
            }
          },
        }).then(() => {
          workbook.xlsx.writeBuffer().then((buffer) => {
            saveAs(new Blob([buffer], { type: "application/octet-stream" }), `${e.fileName}.xlsx`);
          });
        });
      }
    },
    onOptionChanged(e) {
      if (e && e.fullName === "paging.pageSize" && e.value !== e.previousValue) {
        const instance = this.$refs?.smWarehousesGrid?.instance;
        if (instance) {
          instance.option("paging.pageIndex", 0);
        }
        this.reloadGrid();
      }
    },
  },
  mounted() {
    this.getDepartments({
      enablePagination: 0,
      limit: 9999,
    });
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/components/dxSmagriaGrid.scss";
</style>
